import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ArrowDownLeft, Info, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal, Field, CURRENCIES, Segmented } from "./ui";
import TickerSearch from "./TickerSearch";
import BrokerSelect from "./BrokerSelect";
import HoldingSelect from "./HoldingSelect";
import { api } from "../lib/api";
import { money, shortDate, num, isCryptoType } from "../lib/format";
import useGuardedSubmit from "../lib/useGuardedSubmit";

const today = () => new Date().toISOString().slice(0, 10);
// Crypto can be worth a small fraction of a cent — rounding a fetched/converted
// price to 2 decimals would just turn it into 0, so keep up to 9 for it.
const roundPrice = (v, isCrypto) => {
  if (v == null || isNaN(v)) return "";
  const factor = isCrypto ? 1e9 : 100;
  return Math.round(v * factor) / factor;
};

// Fetch a live quote and the USD base price so we can reconvert on currency change.
async function fetchLive(ticker) {
  const { data } = await api.quote(ticker);
  let priceUsd = data.price;
  if (data.currency && data.currency !== "USD") {
    try { const fx = await api.fx(data.currency, "USD"); priceUsd = data.price * fx.data.rate; } catch {}
  }
  return { price: data.price, currency: (data.currency || "USD").toUpperCase(), name: data.name, priceUsd, isCrypto: isCryptoType(data.asset_type) };
}
async function convertFromUsd(priceUsd, ccy, isCrypto) {
  if (!priceUsd) return "";
  if (ccy === "USD") return roundPrice(priceUsd, isCrypto);
  try { const fx = await api.fx("USD", ccy); return roundPrice(priceUsd * fx.data.rate, isCrypto); } catch { return roundPrice(priceUsd, isCrypto); }
}

export function BuyDialog({ open, onClose, ticker = "", name = "", onDone }) {
  const { t } = useTranslation();
  const empty = { ticker, name, purchase_price: "", currency: "USD", entryMode: "qty", quantity: "", amount: "", purchase_date: today(), broker: "", fee: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [priceUsd, setPriceUsd] = useState(null);
  const [liveNative, setLiveNative] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [saving, guardedSubmit] = useGuardedSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Remembers the last instrument picked when this dialog isn't opened for a
  // specific ticker (e.g. the generic "Record Buy" button), so reopening it to
  // log a second buy keeps the field filled in — and actually stays in sync
  // with `form.ticker` this time, instead of just looking filled while empty.
  const lastPick = useRef({ ticker: "", name: "" });

  useEffect(() => {
    if (open) {
      const effTicker = ticker || lastPick.current.ticker;
      const effName = ticker ? name : lastPick.current.name;
      setForm({ ...empty, ticker: effTicker, name: effName });
      setPriceUsd(null); setLiveNative(null);
      api.brokers().then((r) => setBrokers(r.data)).catch(() => {});
      if (effTicker) loadQuote(effTicker);
    }
  }, [open, ticker, name]);

  const loadQuote = async (tk) => {
    try {
      const live = await fetchLive(tk);
      setPriceUsd(live.priceUsd); setLiveNative(live);
      setForm((f) => ({ ...f, currency: live.currency, purchase_price: roundPrice(live.price, live.isCrypto), name: f.name || live.name }));
    } catch {}
  };

  const onPickTicker = ({ ticker: tk, name: nm }) => {
    set("ticker", tk); set("name", nm);
    lastPick.current = { ticker: tk, name: nm };
    if (tk) loadQuote(tk); else { setPriceUsd(null); setLiveNative(null); set("purchase_price", ""); }
  };

  const onCurrency = async (ccy) => {
    set("currency", ccy);
    if (priceUsd) set("purchase_price", await convertFromUsd(priceUsd, ccy, liveNative?.isCrypto));
  };

  const useLive = async () => {
    if (priceUsd) set("purchase_price", await convertFromUsd(priceUsd, form.currency, liveNative?.isCrypto));
  };

  const price = parseFloat(form.purchase_price) || 0;
  const qtyFromAmount = price ? (parseFloat(form.amount) || 0) / price : 0;
  const effectiveQty = form.entryMode === "amount" ? qtyFromAmount : (parseFloat(form.quantity) || 0);
  const totalCost = price * effectiveQty;

  const submit = () => {
    if (!form.ticker || !form.purchase_price || !form.broker || effectiveQty <= 0) {
      toast.error("Ticker, price, broker and quantity/amount are required"); return;
    }
    guardedSubmit(async () => {
      try {
        await api.buy({
          ticker: form.ticker.toUpperCase(), name: form.name, purchase_price: price,
          currency: form.currency, quantity: effectiveQty, purchase_date: form.purchase_date,
          broker: form.broker, fee: parseFloat(form.fee) || 0, notes: form.notes,
        });
        // Best-effort: the backend already no-ops if it's watched already, so this is safe to fire and forget.
        api.addWatch({ ticker: form.ticker.toUpperCase(), name: form.name }).catch(() => {});
        toast.success(t("dialogs.buySaved"));
        onDone && onDone(); onClose();
      } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={t("dialogs.recordBuy")} wide testid="buy-dialog">
      <div className="space-y-4">
        <Field label={t("dialogs.instrument")}>
          <TickerSearch value={form.ticker} name={form.name} onSelect={onPickTicker} testid="buy-ticker-search" />
          {liveNative && (
            <div className="mt-1.5 text-xs text-slate-500 flex items-center gap-1.5">
              <Zap size={12} className="text-brand-500" /> Live: {money(liveNative.price, liveNative.currency, { crypto: liveNative.isCrypto })}
              <button type="button" onClick={useLive} className="text-brand-600 hover:underline ml-1">use current</button>
            </div>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t("dialogs.pricePerShare")}>
            <input data-testid="buy-price-input" type="number" className="input tabular" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} placeholder="0.00" />
          </Field>
          <Field label={t("dialogs.currency")}>
            <select data-testid="buy-currency-select" className="input" value={form.currency} onChange={(e) => onCurrency(e.target.value)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="label">{t("dialogs.entryMode")}</span>
            <Segmented options={[{ value: "qty", label: t("dialogs.byQuantity") }, { value: "amount", label: t("dialogs.byAmount") }]} value={form.entryMode} onChange={(v) => set("entryMode", v)} testid="buy-entry-mode" />
          </div>
          {form.entryMode === "qty" ? (
            <input data-testid="buy-quantity-input" type="number" className="input tabular" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder={t("dialogs.quantity")} />
          ) : (
            <div className="relative">
              <input data-testid="buy-amount-input" type="number" className="input tabular pr-16" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder={`${t("dialogs.totalSpend")} (${form.currency})`} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{form.currency}</span>
            </div>
          )}
          {price > 0 && effectiveQty > 0 && (
            <div className="mt-1.5 text-xs text-slate-500 tabular" data-testid="buy-computed">
              {form.entryMode === "amount"
                ? <>≈ <b>{num(effectiveQty, 4)}</b> units at {money(price, form.currency)}</>
                : <>Total ≈ <b>{money(totalCost, form.currency)}</b></>}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t("dialogs.broker")}>
            <BrokerSelect value={form.broker} onChange={(v) => set("broker", v)} brokers={brokers} testid="buy-broker-select" />
          </Field>
          <Field label={t("dialogs.purchaseDate")}>
            <input data-testid="buy-date-input" type="date" className="input" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} />
          </Field>
        </div>

        <Field label={t("dialogs.feeOptional", { ccy: form.currency })}>
          <input data-testid="buy-fee-input" type="number" className="input tabular" value={form.fee} onChange={(e) => set("fee", e.target.value)} placeholder="0.00" />
        </Field>

        <Field label={t("dialogs.notesOptional")}>
          <textarea data-testid="buy-notes-input" className="input min-h-[70px] resize-none" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Long-term hold; target $250" />
        </Field>
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
        <button data-testid="buy-submit-btn" className="btn-primary" onClick={submit} disabled={saving}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} {t("common.buy")}
        </button>
      </div>
    </Modal>
  );
}

export function SellDialog({ open, onClose, ticker = "", brokerPrefill = "", onDone }) {
  const { t } = useTranslation();
  const empty = { ticker, broker: brokerPrefill, entryMode: "qty", quantity: "", amount: "", sell_price: "", currency: "USD", sell_date: today(), fee: "", notes: "", name: "" };
  const [form, setForm] = useState(empty);
  const [priceUsd, setPriceUsd] = useState(null);
  const [liveNative, setLiveNative] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [lots, setLots] = useState([]);
  const [available, setAvailable] = useState(0);
  const [saving, guardedSubmit] = useGuardedSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm({ ...empty, ticker, broker: brokerPrefill });
      setPriceUsd(null); setLiveNative(null);
      api.holdings().then((r) => setHoldings(r.data)).catch(() => setHoldings([]));
      if (ticker) loadQuote(ticker);
    }
  }, [open, ticker, brokerPrefill]);

  const loadQuote = async (tk) => {
    try {
      const live = await fetchLive(tk);
      setPriceUsd(live.priceUsd); setLiveNative(live);
      setForm((f) => ({ ...f, currency: live.currency, sell_price: roundPrice(live.price, live.isCrypto), name: live.name }));
    } catch {}
  };

  // Only brokers that actually hold this ticker — selling from a broker with
  // nothing to sell doesn't make sense, and BrokerSelect auto-picks it if there's just one.
  const currentHolding = holdings.find((h) => h.ticker === form.ticker);
  const brokersForTicker = currentHolding ? [...new Set(currentHolding.lots.map((l) => l.broker).filter(Boolean))] : [];
  const isCrypto = isCryptoType(currentHolding?.asset_type) || !!liveNative?.isCrypto;

  useEffect(() => {
    if (open && form.ticker && form.broker) {
      api.availableLots(form.ticker.toUpperCase(), form.broker)
        .then((r) => { setLots(r.data.lots); setAvailable(r.data.total_available); })
        .catch(() => { setLots([]); setAvailable(0); });
    } else { setLots([]); setAvailable(0); }
  }, [open, form.ticker, form.broker]);

  const onCurrency = async (ccy) => { set("currency", ccy); if (priceUsd) set("sell_price", await convertFromUsd(priceUsd, ccy, isCrypto)); };
  const useLive = async () => { if (priceUsd) set("sell_price", await convertFromUsd(priceUsd, form.currency, isCrypto)); };

  const sellPrice = parseFloat(form.sell_price) || 0;
  const qtyFromAmount = sellPrice ? (parseFloat(form.amount) || 0) / sellPrice : 0;
  const sellQty = form.entryMode === "amount" ? qtyFromAmount : (parseFloat(form.quantity) || 0);
  let remaining = sellQty;
  const consumed = lots.map((l) => { const take = Math.min(l.remaining_quantity, remaining); remaining -= take; return { ...l, take }; }).filter((l) => l.take > 0);

  // The UI only ever shows the available balance rounded to 4 decimals, so retyping
  // exactly what's displayed can be a hair above the true (more precise) remaining
  // quantity. Tolerate that gap here too — the backend does the same — and clamp
  // down to what's actually there rather than reject a "sell everything".
  const QTY_DISPLAY_TOLERANCE = 1e-4;
  const sellAll = () => { set("entryMode", "qty"); set("quantity", available); };

  const submit = () => {
    if (!form.ticker || !form.broker || sellQty <= 0 || !form.sell_price) { toast.error(t("dialogs.sellRequired")); return; }
    if (sellQty > available + QTY_DISPLAY_TOLERANCE) { toast.error(t("dialogs.onlyAvailable", { available, broker: form.broker })); return; }
    const qtyToSend = Math.min(sellQty, available);
    guardedSubmit(async () => {
      try {
        await api.sell({ ticker: form.ticker.toUpperCase(), quantity: qtyToSend, sell_price: sellPrice, currency: form.currency, sell_date: form.sell_date, broker: form.broker, fee: parseFloat(form.fee) || 0, notes: form.notes });
        toast.success(t("dialogs.sellSaved"));
        onDone && onDone(); onClose();
      } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={`${t("dialogs.recordSell")} (FIFO)`} wide testid="sell-dialog">
      <div className="grid grid-cols-2 gap-4">
        <Field label={t("dialogs.instrument")}>
          <HoldingSelect value={form.ticker} holdings={holdings} onSelect={({ ticker: tk, name }) => { set("ticker", tk); set("name", name); set("broker", ""); if (tk) loadQuote(tk); }} testid="sell-ticker-search" />
        </Field>
        <Field label={t("dialogs.broker")}>
          <BrokerSelect value={form.broker} onChange={(v) => set("broker", v)} brokers={brokersForTicker} testid="sell-broker-select" />
        </Field>
      </div>

      {form.broker && form.ticker && (
        <div className="mt-4 card p-4 bg-brand-50/40 border-brand-100">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 text-sm text-brand-700">
              <Info size={15} /> {t("dialogs.availableIn", { broker: form.broker })} <b className="tabular">{num(available, 4, { crypto: isCrypto })}</b> {t("dialogs.shares")}
            </div>
            <button type="button" data-testid="sell-all-btn" onClick={sellAll} className="text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline shrink-0">
              {t("dialogs.sellAll")}
            </button>
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {lots.length === 0 && <div className="text-sm text-slate-400">{t("dialogs.noLots")}</div>}
            {lots.map((l) => {
              const c = consumed.find((x) => x.id === l.id);
              return (
                <div key={l.id} className={`flex items-center justify-between text-sm rounded-lg px-3 py-2 ${c ? "bg-brand-100/70" : "bg-white"}`}>
                  <span className="text-slate-600">{shortDate(l.purchase_date)} · {num(l.remaining_quantity, 4, { crypto: isCrypto })} @ {money(l.purchase_price, l.currency, { crypto: isCrypto })}</span>
                  {c && <span className="pill bg-brand-500 text-white flex items-center gap-1"><ArrowDownLeft size={12} /> {t("dialogs.selling", { qty: num(c.take, 4, { crypto: isCrypto }) })}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="label">{t("dialogs.entryMode")}</span>
          <Segmented options={[{ value: "qty", label: t("dialogs.byQuantity") }, { value: "amount", label: t("dialogs.byAmount") }]} value={form.entryMode} onChange={(v) => set("entryMode", v)} testid="sell-entry-mode" />
        </div>
        {form.entryMode === "qty" ? (
          <input data-testid="sell-quantity-input" type="number" className="input tabular" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="0" />
        ) : (
          <div className="relative">
            <input data-testid="sell-amount-input" type="number" className="input tabular pr-16" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder={`${t("dialogs.totalSpend")} (${form.currency})`} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{form.currency}</span>
          </div>
        )}
        {sellPrice > 0 && sellQty > 0 && form.entryMode === "amount" && (
          <div className="mt-1.5 text-xs text-slate-500 tabular" data-testid="sell-computed">
            ≈ <b>{num(sellQty, 4, { crypto: isCrypto })}</b> {t("dialogs.shares")}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <Field label={t("dialogs.pricePerShare")}>
          <input data-testid="sell-price-input" type="number" className="input tabular" value={form.sell_price} onChange={(e) => set("sell_price", e.target.value)} placeholder="0.00" />
          {liveNative && <button type="button" onClick={useLive} className="mt-1 text-xs text-brand-600 hover:underline">use current {money(liveNative.price, liveNative.currency, { crypto: isCrypto })}</button>}
        </Field>
        <Field label={t("dialogs.currency")}>
          <select data-testid="sell-currency-select" className="input" value={form.currency} onChange={(e) => onCurrency(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label={t("common.date")}>
          <input data-testid="sell-date-input" type="date" className="input" value={form.sell_date} onChange={(e) => set("sell_date", e.target.value)} />
        </Field>
        <Field label={t("dialogs.feeOptional", { ccy: form.currency })}>
          <input data-testid="sell-fee-input" type="number" className="input tabular" value={form.fee} onChange={(e) => set("fee", e.target.value)} placeholder="0.00" />
        </Field>
      </div>
      <div className="mt-4">
        <Field label={t("dialogs.notesOptional")}><textarea data-testid="sell-notes-input" className="input min-h-[60px] resize-none" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
      </div>
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
        <button data-testid="sell-submit-btn" className="btn-primary" onClick={submit} disabled={saving}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} {t("common.sell")}
        </button>
      </div>
    </Modal>
  );
}

// Editing a buy/sell keeps the ticker fixed (and, for a sell, the broker too) —
// changing either would mean picking different FIFO lots entirely, so that's a
// delete-and-re-enter, not an edit. Only the surrounding details are editable here.
export function EditBuyDialog({ open, onClose, tx, onDone }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ purchase_price: "", currency: "USD", entryMode: "qty", quantity: "", amount: "", purchase_date: "", broker: "", fee: "", notes: "" });
  const [brokers, setBrokers] = useState([]);
  const [saving, guardedSubmit] = useGuardedSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && tx) {
      setForm({
        purchase_price: tx.price, currency: tx.currency, entryMode: "qty", quantity: tx.quantity, amount: "",
        purchase_date: tx.date, broker: tx.broker, fee: tx.fee || "", notes: tx.notes || "",
      });
      api.brokers().then((r) => setBrokers(r.data)).catch(() => {});
    }
  }, [open, tx]);

  const price = parseFloat(form.purchase_price) || 0;
  const qtyFromAmount = price ? (parseFloat(form.amount) || 0) / price : 0;
  const effectiveQty = form.entryMode === "amount" ? qtyFromAmount : (parseFloat(form.quantity) || 0);
  const totalCost = price * effectiveQty;

  const submit = () => {
    if (!form.purchase_price || effectiveQty <= 0 || !form.broker || !form.purchase_date) { toast.error(t("dialogs.failed")); return; }
    guardedSubmit(async () => {
    try {
      await api.updateBuy(tx.id, {
        purchase_price: price, currency: form.currency,
        quantity: effectiveQty, purchase_date: form.purchase_date,
        broker: form.broker, fee: parseFloat(form.fee) || 0, notes: form.notes,
      });
      toast.success(t("dialogs.saved"));
      onDone && onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    });
  };

  if (!tx) return null;
  return (
    <Modal open={open} onClose={onClose} title={t("dialogs.editBuy")} testid="edit-buy-dialog">
      <div className="space-y-4">
        <div className="text-sm text-slate-500">
          <span className="font-medium text-slate-900">{tx.ticker}</span> · {tx.name}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("dialogs.pricePerShare")}>
            <input data-testid="edit-buy-price-input" type="number" className="input tabular" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} />
          </Field>
          <Field label={t("dialogs.currency")}>
            <select data-testid="edit-buy-currency-select" className="input" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="label">{t("dialogs.entryMode")}</span>
            <Segmented options={[{ value: "qty", label: t("dialogs.byQuantity") }, { value: "amount", label: t("dialogs.byAmount") }]} value={form.entryMode} onChange={(v) => set("entryMode", v)} testid="edit-buy-entry-mode" />
          </div>
          {form.entryMode === "qty" ? (
            <input data-testid="edit-buy-quantity-input" type="number" className="input tabular" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
          ) : (
            <div className="relative">
              <input data-testid="edit-buy-amount-input" type="number" className="input tabular pr-16" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder={`${t("dialogs.totalSpend")} (${form.currency})`} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{form.currency}</span>
            </div>
          )}
          {price > 0 && effectiveQty > 0 && (
            <div className="mt-1.5 text-xs text-slate-500 tabular" data-testid="edit-buy-computed">
              {form.entryMode === "amount"
                ? <>≈ <b>{num(effectiveQty, 4, { crypto: isCryptoType(tx.asset_type) })}</b> units at {money(price, form.currency)}</>
                : <>Total ≈ <b>{money(totalCost, form.currency)}</b></>}
            </div>
          )}
        </div>
        {tx.quantity !== tx.remaining_quantity && (
          <div className="text-xs text-amber-600 -mt-2">{t("dialogs.alreadySoldNote", { qty: num(tx.quantity - tx.remaining_quantity, 4, { crypto: isCryptoType(tx.asset_type) }) })}</div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("dialogs.broker")}>
            <BrokerSelect value={form.broker} onChange={(v) => set("broker", v)} brokers={brokers} testid="edit-buy-broker-select" />
          </Field>
          <Field label={t("dialogs.purchaseDate")}>
            <input data-testid="edit-buy-date-input" type="date" className="input" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} />
          </Field>
        </div>
        <Field label={t("dialogs.feeOptional", { ccy: form.currency })}>
          <input data-testid="edit-buy-fee-input" type="number" className="input tabular" value={form.fee} onChange={(e) => set("fee", e.target.value)} placeholder="0.00" />
        </Field>
        <Field label={t("dialogs.notesOptional")}>
          <textarea data-testid="edit-buy-notes-input" className="input min-h-[60px] resize-none" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>
      </div>
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
        <button data-testid="edit-buy-submit-btn" className="btn-primary" onClick={submit} disabled={saving}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} {t("common.save")}
        </button>
      </div>
    </Modal>
  );
}

export function EditSellDialog({ open, onClose, tx, onDone }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ entryMode: "qty", quantity: "", amount: "", sell_price: "", currency: "USD", sell_date: "", fee: "", notes: "" });
  const [available, setAvailable] = useState(0);
  const [saving, guardedSubmit] = useGuardedSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && tx) {
      setForm({ entryMode: "qty", quantity: tx.quantity, amount: "", sell_price: tx.price, currency: tx.currency, sell_date: tx.date, fee: tx.fee || "", notes: tx.notes || "" });
      // What's still open, plus what this sell already consumed — that total is the
      // real ceiling when editing, since the edit releases its own lots first.
      api.availableLots(tx.ticker, tx.broker)
        .then((r) => setAvailable((r.data.total_available || 0) + (tx.quantity || 0)))
        .catch(() => setAvailable(tx.quantity || 0));
    }
  }, [open, tx]);

  const sellPrice = parseFloat(form.sell_price) || 0;
  const qtyFromAmount = sellPrice ? (parseFloat(form.amount) || 0) / sellPrice : 0;
  const effectiveQty = form.entryMode === "amount" ? qtyFromAmount : (parseFloat(form.quantity) || 0);
  const isCrypto = isCryptoType(tx?.asset_type);

  const submit = () => {
    if (effectiveQty <= 0 || !form.sell_price || !form.sell_date) { toast.error(t("dialogs.failed")); return; }
    guardedSubmit(async () => {
      try {
        await api.updateSell(tx.id, {
          quantity: effectiveQty, sell_price: parseFloat(form.sell_price),
          currency: form.currency, sell_date: form.sell_date, fee: parseFloat(form.fee) || 0, notes: form.notes,
        });
        toast.success(t("dialogs.saved"));
        onDone && onDone(); onClose();
      } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    });
  };

  if (!tx) return null;
  return (
    <Modal open={open} onClose={onClose} title={t("dialogs.editSell")} testid="edit-sell-dialog">
      <div className="space-y-4">
        <div className="text-sm text-slate-500">
          <span className="font-medium text-slate-900">{tx.ticker}</span> · {tx.name} · {tx.broker}
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="label">{t("dialogs.entryMode")}</span>
            <div className="flex items-center gap-3">
              {available > 0 && (
                <button type="button" data-testid="edit-sell-all-btn"
                        onClick={() => { set("entryMode", "qty"); set("quantity", available); }}
                        className="text-xs font-medium text-brand-600 hover:underline">
                  {t("dialogs.sellAll")}
                </button>
              )}
              <Segmented options={[{ value: "qty", label: t("dialogs.byQuantity") }, { value: "amount", label: t("dialogs.byAmount") }]}
                         value={form.entryMode} onChange={(v) => set("entryMode", v)} testid="edit-sell-entry-mode" />
            </div>
          </div>
          {form.entryMode === "qty" ? (
            <input data-testid="edit-sell-quantity-input" type="number" className="input tabular" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
          ) : (
            <div className="relative">
              <input data-testid="edit-sell-amount-input" type="number" className="input tabular pr-16" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder={`${t("dialogs.totalSpend")} (${form.currency})`} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{form.currency}</span>
            </div>
          )}
          {sellPrice > 0 && effectiveQty > 0 && form.entryMode === "amount" && (
            <div className="mt-1.5 text-xs text-slate-500 tabular" data-testid="edit-sell-computed">
              ≈ <b>{num(effectiveQty, 4, { crypto: isCrypto })}</b> {t("dialogs.shares")}
            </div>
          )}
          {available > 0 && (
            <div className="mt-1.5 text-xs text-slate-400">
              {t("dialogs.availableEditing", { qty: num(available, 4, { crypto: isCrypto }) })}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t("dialogs.pricePerShare")}>
            <input data-testid="edit-sell-price-input" type="number" className="input tabular" value={form.sell_price} onChange={(e) => set("sell_price", e.target.value)} />
          </Field>
          <Field label={t("dialogs.currency")}>
            <select data-testid="edit-sell-currency-select" className="input" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={t("common.date")}>
            <input data-testid="edit-sell-date-input" type="date" className="input" value={form.sell_date} onChange={(e) => set("sell_date", e.target.value)} />
          </Field>
          <Field label={t("dialogs.feeOptional", { ccy: form.currency })}>
            <input data-testid="edit-sell-fee-input" type="number" className="input tabular" value={form.fee} onChange={(e) => set("fee", e.target.value)} placeholder="0.00" />
          </Field>
        </div>
        <Field label={t("dialogs.notesOptional")}>
          <textarea data-testid="edit-sell-notes-input" className="input min-h-[60px] resize-none" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>
        <div className="text-xs text-slate-400">{t("dialogs.editSellNote")}</div>
      </div>
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
        <button data-testid="edit-sell-submit-btn" className="btn-primary" onClick={submit} disabled={saving}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} {t("common.save")}
        </button>
      </div>
    </Modal>
  );
}

export function AlertDialog({ open, onClose, ticker = "", name = "", onDone }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ticker, name, condition: "above", target_price: "" });
  const [live, setLive] = useState(null);
  const [saving, guardedSubmit] = useGuardedSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) { setForm({ ticker, name, condition: "above", target_price: "" }); setLive(null); if (ticker) loadQuote(ticker); }
  }, [open, ticker, name]);

  const loadQuote = async (tk) => {
    try { const { data } = await api.quote(tk); setLive(data); setForm((f) => ({ ...f, target_price: roundPrice(data.price, isCryptoType(data.asset_type)) })); } catch {}
  };

  const submit = () => {
    if (!form.ticker || !form.target_price) { toast.error("Ticker and target price required"); return; }
    guardedSubmit(async () => {
      try {
        await api.createAlert({ ticker: form.ticker.toUpperCase(), name: form.name, condition: form.condition, target_price: parseFloat(form.target_price) });
        toast.success(t("dialogs.alertCreated")); onDone && onDone(); onClose();
      } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={t("dialogs.createAlert")} testid="alert-dialog">
      <Field label={t("dialogs.instrument")}>
        <TickerSearch value={form.ticker} name={form.name} onSelect={({ ticker: tk, name }) => { set("ticker", tk); set("name", name); if (tk) loadQuote(tk); }} testid="alert-ticker-search" />
        {live && <div className="mt-1.5 text-xs text-slate-500">{t("insights.current")}: {money(live.price, live.currency, { crypto: isCryptoType(live.asset_type) })}</div>}
      </Field>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <Field label={t("dialogs.condition")}>
          <select data-testid="alert-condition-select" className="input" value={form.condition} onChange={(e) => set("condition", e.target.value)}>
            <option value="above">{t("dialogs.priceAbove")}</option>
            <option value="below">{t("dialogs.priceBelow")}</option>
          </select>
        </Field>
        <Field label={t("dialogs.targetPrice")}>
          <input data-testid="alert-target-input" type="number" className="input tabular" value={form.target_price} onChange={(e) => set("target_price", e.target.value)} placeholder="0.00" />
        </Field>
      </div>
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
        <button data-testid="alert-submit-btn" className="btn-primary" onClick={submit} disabled={saving}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} {t("alerts.newAlert")}
        </button>
      </div>
    </Modal>
  );
}
